import { Sites } from '@ps/platform-message-framework/common/Sites';
import React from 'react';
import { useSelector } from 'react-redux';

import type { StatusCode } from '@swf/core/enums/statusCode';
import { select } from '@swf/core/store';

import { ExclusionType } from '../definitions';
import useExclusionType from '../hooks/useExclusionType';

import DefaultBreakResponse from './DefaultBreakResponse/DefaultBreakResponse';
import DefaultSelfExclusionResponse from './DefaultSelfExclusionResponse/DefaultSelfExclusionResponse';
import ExclusionResponseCAON from './ExclusionResponseCAON/ExclusionResponseCAON';
import ExclusionResponseCOM from './ExclusionResponseCOM/ExclusionResponseCOM';
import ExclusionResponseDE from './ExclusionResponseDE/ExclusionResponseDE';
import ExclusionResponseDK from './ExclusionResponseDK/ExclusionResponseDK';
import ExclusionResponseFR from './ExclusionResponseFR/ExclusionResponseFR';
import ExclusionResponseGR from './ExclusionResponseGR/ExclusionResponseGR';
import ExclusionResponseIT from './ExclusionResponseIT/ExclusionResponseIT';
import ExclusionResponsePT from './ExclusionResponsePT/ExclusionResponsePT';
import ExclusionResponseUK from './ExclusionResponseUK/ExclusionResponseUK';
import ExclusionResponseUSMI from './ExclusionResponseUSMI/ExclusionResponseUSMI';
import ExclusionResponseUSNJ from './ExclusionResponseUSNJ/ExclusionResponseUSNJ';
import ExclusionResponseUSPA from './ExclusionResponseUSPA/ExclusionResponseUSPA';

interface Props {
  error?: boolean;
  daysRequested?: number;
  timeUntil?: number | string;
  banEndDate?: string;
  showExclusionConfirmationDE?: boolean;
  statusCode?: StatusCode;
  selfExcludedInfoFlags?: number;
}

const ExclusionResponse: React.FC<Props> = ({
  error,
  timeUntil,
  banEndDate,
  daysRequested,
  showExclusionConfirmationDE,
  statusCode,
  selfExcludedInfoFlags
}) => {
  const site = useSelector(select.cms.getSiteId);
  const nationalExclusion = useSelector(select.account.bannedByRegulator);
  const exclusionType = useExclusionType(daysRequested, nationalExclusion);

  const selfExclusionEndDate = timeUntil ? new Date(timeUntil) : new Date();

  switch (true) {
    case site === Sites.FRA:
      return (
        <ExclusionResponseFR error={error} endDate={selfExclusionEndDate} statusCode={statusCode} />
      );
    case site === Sites.GR:
      return (
        <ExclusionResponseGR
          error={error}
          timeUntil={timeUntil}
          statusCode={statusCode}
          daysRequested={daysRequested}
        />
      );
    case site === Sites.US_PA:
      return (
        <ExclusionResponseUSPA
          error={error}
          endDate={selfExclusionEndDate}
          statusCode={statusCode}
          daysRequested={daysRequested}
          exclusionType={exclusionType}
        />
      );

    case site === Sites.US_NJ:
      return (
        <ExclusionResponseUSNJ
          error={error}
          timeUntil={timeUntil}
          statusCode={statusCode}
          daysRequested={daysRequested}
          exclusionType={exclusionType}
        />
      );
    case site === Sites.USMI:
      return (
        <ExclusionResponseUSMI
          error={error}
          timeUntil={timeUntil}
          statusCode={statusCode}
          daysRequested={daysRequested}
          exclusionType={exclusionType}
        />
      );
    case site === Sites.POR:
      return (
        <ExclusionResponsePT
          error={error}
          statusCode={statusCode}
          exclusionType={exclusionType}
          timeUntil={timeUntil}
          selfExcludedInfoFlags={selfExcludedInfoFlags}
          daysRequested={daysRequested}
        />
      );
    case site === Sites.DEN:
      return (
        <ExclusionResponseDK
          error={error}
          statusCode={statusCode}
          exclusionType={exclusionType}
          timeUntil={timeUntil}
          daysRequested={daysRequested}
        />
      );
    case site === Sites.DE:
      return (
        <ExclusionResponseDE
          error={error}
          confirmationEndDate={banEndDate}
          showExclusionConfirmationDE={showExclusionConfirmationDE}
          daysRequested={daysRequested}
          exclusionType={exclusionType}
          endDate={selfExclusionEndDate}
        />
      );
    case site === Sites.UK:
      return (
        <ExclusionResponseUK
          daysRequested={daysRequested}
          exclusionType={exclusionType}
          endDate={selfExclusionEndDate}
        />
      );
    case site === Sites.CA_ON:
      return (
        <ExclusionResponseCAON
          daysRequested={daysRequested}
          exclusionType={exclusionType}
          endDate={selfExclusionEndDate}
        />
      );
    case site === Sites.ITA:
      return (
        <ExclusionResponseIT
          daysRequested={daysRequested}
          exclusionType={exclusionType}
          endDate={selfExclusionEndDate}
        />
      );
    case site === Sites.COM:
      return (
        <ExclusionResponseCOM
          daysRequested={daysRequested}
          exclusionType={exclusionType}
          endDate={selfExclusionEndDate}
        />
      );
    case exclusionType === ExclusionType.Break:
      return <DefaultBreakResponse daysRequested={daysRequested} endDate={selfExclusionEndDate} />;
    default:
      return (
        <DefaultSelfExclusionResponse
          daysRequested={daysRequested}
          exclusionType={exclusionType}
          endDate={selfExclusionEndDate}
        />
      );
  }
};

export default ExclusionResponse;
